import type { Tables } from '../../types/database.types';

/**
 * My Tasks (post-v1.0.0 enhancement): one list of everything assigned to the
 * signed-in person, across every project they can see. Row Level Security
 * scopes the read; this module only arranges what comes back.
 */

export type TaskPriority = Tables<'tasks'>['priority'];

/** One assigned task with just enough context to stand outside its project. */
export interface MyTask {
  id: string;
  /** Null for a deal task: it belongs to a deal, not a project. */
  project_id: string | null;
  /** Set for a deal task, whose home is the deal drawer, not a project page. */
  deal_id: string | null;
  parent_task_id: string | null;
  title: string;
  priority: TaskPriority;
  due_date: string | null;
  is_completed: boolean;
  /**
   * The owning project's name, and whether it has been archived. Null for a
   * deal task, which belongs to a deal and so to no project at all.
   */
  projects: { name: string; is_archived: boolean } | null;
  /** The deal's name, for a deal task. Null for a project task. */
  deals: { name: string } | null;
  project_statuses: { name: string; color_key: string } | null;
}

/**
 * Where a task row goes when clicked. A deal task has no project, and until
 * 22 September this linked to `/projects/null`, which is the "This project
 * could not be loaded" page Kyu hit from My Tasks. A deal task opens its deal
 * instead, at the same `/deals?deal=<id>` address the Inbox and Sales Tasks
 * already use. Null when a task points at neither, so the row shows plain
 * text rather than a link to an error page.
 */
export function myTaskHref(task: Pick<MyTask, 'project_id' | 'deal_id'>): string | null {
  if (task.project_id !== null) return `/projects/${task.project_id}`;
  if (task.deal_id !== null) return `/deals?deal=${encodeURIComponent(task.deal_id)}`;
  return null;
}

/** The context line under the title: the project, or the deal. */
export function myTaskHome(task: Pick<MyTask, 'projects' | 'deals' | 'deal_id'>): string {
  if (task.projects !== null) return task.projects.name;
  if (task.deals !== null) return `Deal · ${task.deals.name}`;
  // A deal this person cannot read still names the kind of home it has.
  if (task.deal_id !== null) return 'Deal';
  return 'Unavailable project';
}

export type DueBucketKey = 'overdue' | 'today' | 'upcoming' | 'someday';

export const DUE_BUCKET_LABELS: Record<DueBucketKey, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  upcoming: 'Upcoming',
  someday: 'No due date',
};

export interface DueBucket {
  key: DueBucketKey;
  tasks: MyTask[];
}

/** A calendar date in the person's own timezone, for due-date comparison. */
export function localIsoDate(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Open tasks arranged into due-date buckets, each sorted soonest first
 * (someday keeps the fetch order). Empty buckets are dropped so the page
 * shows only groups that exist.
 */
export function groupByDue(tasks: MyTask[], todayIso: string): DueBucket[] {
  const open = tasks.filter((task) => !task.is_completed);
  const buckets: Record<DueBucketKey, MyTask[]> = {
    overdue: [],
    today: [],
    upcoming: [],
    someday: [],
  };
  for (const task of open) {
    if (task.due_date === null) {
      buckets.someday.push(task);
    } else if (task.due_date < todayIso) {
      buckets.overdue.push(task);
    } else if (task.due_date === todayIso) {
      buckets.today.push(task);
    } else {
      buckets.upcoming.push(task);
    }
  }
  const byDue = (a: MyTask, b: MyTask) => (a.due_date ?? '').localeCompare(b.due_date ?? '');
  buckets.overdue.sort(byDue);
  buckets.today.sort(byDue);
  buckets.upcoming.sort(byDue);
  return (Object.keys(DUE_BUCKET_LABELS) as DueBucketKey[])
    .map((key) => ({ key, tasks: buckets[key] }))
    .filter((bucket) => bucket.tasks.length > 0);
}
